import { Callout, MenuItem } from "@blueprintjs/core"
import { useDebouncedValue } from "@mantine/hooks"
import StaticSelector from "components/Atoms/StaticSelector"
import { useSellableItemStockSelectorQuery } from "generated/graphql"
import React, { useEffect, useState } from "react"
import { getAvailableStock } from "./OrderItem"

const StockSelector = ({
  orderItems,
  path,
  sellableItem,
  isConsolidated,
  distributionCentres,
  className = "",
  userGroups,
  requireUserGroup = false,
}) => {
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebouncedValue(search, 500)

  // When requireUserGroup is true, both a user and a target DC must be selected
  // before fetching. When false (default), only a DC is required.
  const hasUser = !!userGroups
  const hasTargetDc = !!distributionCentres?.[0]
  const canFetch = requireUserGroup ? hasUser && hasTargetDc : hasTargetDc

  const userGroupIds = userGroups?.groups?.map((g: any) => g.id) ?? []

  const { data, refetch } = useSellableItemStockSelectorQuery({
    variables: {
      first: 20,
      query: debouncedSearch,
      filter: {
        isLive: true,
        distributionCentres,
        ...(requireUserGroup ? { groupVisibility: userGroupIds } : {}),
      },
    },
    skip: !canFetch,
  })

  // Re-fetch stock when the user selection changes
  useEffect(() => {
    if (canFetch) {
      refetch()
    }
  }, [userGroups])

  // Show error callout only when requireUserGroup is enabled and prerequisites are missing
  if (requireUserGroup && !canFetch) {
    const missing: string[] = []
    if (!hasUser) missing.push("User")
    if (!hasTargetDc) missing.push("Target DC")

    return (
      <Callout intent="warning" icon="warning-sign" className={className}>
        Please select {missing.join(" and ")}.
      </Callout>
    )
  }

  const items = data?.sellableItemStock || []

  return (
    <StaticSelector
      items={items}
      query={search}
      onQueryChange={setSearch}
      popoverClassName="vt-stock-selector-popover"
      className={className}
      searchVector={["sellableItem.name", "distributionCentre.name"]}
      itemRenderer={(item, { handleClick, modifiers }) => {
        const isPreviouslySelected = orderItems.some(
          oi => oi?.sellableItemStock?.id === item.id
        )
        const availableStock = getAvailableStock(item, isConsolidated)
        return (
          <MenuItem
            disabled={isPreviouslySelected || !availableStock}
            active={modifiers.active}
            key={item.id}
            onClick={handleClick}
            text={item?.sellableItem?.name}
            labelElement={availableStock}
          />
        )
      }}
      renderText={item =>
        item?.sellableItem?.name || sellableItem?.name || "Select"
      }
      path={`${path}.sellableItemStock`}
    />
  )
}

export default StockSelector
